/**
 * Case-insensitive printer identity + verified alias map (after successful silent print).
 * Helps when Chromium name ≠ Windows queue casing / display suffix.
 */

import { kvGet, kvSet } from '../infrastructure/storage/kvAccess';
import { printerDeviceId, printerDisplayLabel } from './printerIdentity';

export const PRINTER_DEVICE_ALIASES_KEY = 'aman:printerDeviceAliases';

function lowerKey(id) {
  return String(id || '')
    .trim()
    .toLowerCase();
}

function readAliasMap() {
  try {
    const raw = kvGet(PRINTER_DEVICE_ALIASES_KEY);
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
    return { ...raw };
  } catch {
    return {};
  }
}

function writeAliasMap(map) {
  try {
    kvSet(PRINTER_DEVICE_ALIASES_KEY, map || {});
  } catch {
    /* ignore */
  }
}

/**
 * Case-insensitive equality for printer ids.
 */
export function printerIdsEqual(a, b) {
  const x = lowerKey(a);
  const y = lowerKey(b);
  return Boolean(x && y && x === y);
}

/**
 * Find printer in list: exact, then case-insensitive name/deviceName.
 * @param {Array<object>} printers
 * @param {string} id
 */
export function findPrinterByDeviceIdLoose(printers, id) {
  const want = String(id || '').trim();
  if (!want) return null;
  const list = Array.isArray(printers) ? printers : [];

  const exact = list.find((p) => {
    const a = String(p?.deviceName || '').trim();
    const b = String(p?.name || '').trim();
    return a === want || b === want;
  });
  if (exact) return exact;

  const wantL = lowerKey(want);
  return (
    list.find((p) => {
      const a = lowerKey(p?.deviceName);
      const b = lowerKey(p?.name);
      return a === wantL || b === wantL;
    }) || null
  );
}

/**
 * Resolve a preferred id against live printers + verified aliases.
 * @returns {{ deviceName: string|null, matched: boolean, source: 'list'|'alias'|'preferred' }}
 */
export function resolveDeviceNameAgainstPrinters(preferredId, printers = []) {
  const preferred = String(preferredId || '').trim();
  if (!preferred) {
    return { deviceName: null, matched: false, source: 'preferred' };
  }

  const listHit = findPrinterByDeviceIdLoose(printers, preferred);
  if (listHit) {
    return {
      deviceName: printerDeviceId(listHit) || preferred,
      matched: true,
      source: 'list',
      displayName: printerDisplayLabel(listHit) || preferred
    };
  }

  const map = readAliasMap();
  const entry = map[lowerKey(preferred)];
  if (entry?.canonical) {
    const aliasHit = findPrinterByDeviceIdLoose(printers, entry.canonical);
    if (aliasHit) {
      return {
        deviceName: printerDeviceId(aliasHit) || entry.canonical,
        matched: true,
        source: 'alias',
        displayName: printerDisplayLabel(aliasHit) || entry.canonical
      };
    }
    // Try each stored alias
    for (const alias of entry.aliases || []) {
      const hit = findPrinterByDeviceIdLoose(printers, alias);
      if (hit) {
        return {
          deviceName: printerDeviceId(hit) || alias,
          matched: true,
          source: 'alias',
          displayName: printerDisplayLabel(hit) || alias
        };
      }
    }
  }

  return { deviceName: preferred, matched: false, source: 'preferred' };
}

/**
 * Record successful print target so future resolves can map similar strings.
 * @param {string} deviceName - name that worked with webContents.print
 * @param {{ preferred?: string, aliases?: string[] }} [opts]
 */
export function recordVerifiedPrinterDevice(deviceName, opts = {}) {
  const canonical = String(deviceName || '').trim();
  if (!canonical) return null;

  const map = readAliasMap();
  const key = lowerKey(canonical);
  const prev = map[key] && typeof map[key] === 'object' ? map[key] : {};
  const aliases = new Set([
    ...(Array.isArray(prev.aliases) ? prev.aliases : []),
    ...(Array.isArray(opts.aliases) ? opts.aliases : [])
  ]);
  const preferred = String(opts.preferred || '').trim();
  if (preferred && !printerIdsEqual(preferred, canonical)) {
    aliases.add(preferred);
  }
  aliases.delete(canonical);

  map[key] = {
    canonical,
    aliases: [...aliases].slice(0, 12),
    lastVerifiedAt: new Date().toISOString()
  };

  // Also index preferred under its own key → canonical
  if (preferred && !printerIdsEqual(preferred, canonical)) {
    const pKey = lowerKey(preferred);
    const pPrev = map[pKey] && typeof map[pKey] === 'object' ? map[pKey] : {};
    const pAliases = new Set([
      ...(Array.isArray(pPrev.aliases) ? pPrev.aliases : []),
      preferred,
      canonical
    ]);
    map[pKey] = {
      canonical,
      aliases: [...pAliases].filter((a) => !printerIdsEqual(a, canonical)).slice(0, 12),
      lastVerifiedAt: new Date().toISOString()
    };
  }

  writeAliasMap(map);
  return map[key];
}
